
import React, { Suspense, lazy, useState, useEffect, useRef, useReducer } from 'react';
import { dataReducer, initialDataState, DataState } from './dataReducer';
import { io, Socket } from 'socket.io-client';
import { motion, AnimatePresence } from 'motion/react';
import { Icons, COLORS } from './constants';
import { Job, JobStatus, Lead, Tree, TreeHealth, Crew, Expense, Drone, DroneMission, Activity, User, Contact, FinancialRecord, TimeEntry, ContractorInvoice, LedgerEntry, MissionTemplate, FlightLog, MissionReport, NotificationItem, NotificationType, InventoryItem } from './types';
import { db, auth } from './firebase';
import { collection, onSnapshot, addDoc, updateDoc, doc, setDoc, deleteDoc, getDocs, getDoc, query, orderBy, limit } from 'firebase/firestore';
import { onAuthStateChanged, signOut, getRedirectResult } from 'firebase/auth';
import DashboardView from './components/DashboardView';
import { Logo, BrandName } from './components/Logo';

import { handleFirestoreError, isMissingDocumentError, OperationType } from './lib/firestore-errors';
import { beacon } from './lib/beacon';
import { sanitizeForFirestore } from './lib/utils';
import { notificationMeta } from './lib/notificationMeta';
import { useOperationsAlerts } from './lib/operationsAlerts';
import { logChange, ChangeLogEntry } from './lib/changeLog';
import InventoryView from './components/InventoryView';
import ChangeLogView from './components/ChangeLogView';
import { apiUrl, API_BASE, authFetch } from './api';
import LeadsView from './components/LeadsView';
import JobsView from './components/JobsView';
import FinanceView from './components/FinanceView';
import ClientPortalView from './components/ClientPortalView';
import StaffClientPreviewView from './components/StaffClientPreviewView';
import CrewDashboardView from './components/CrewDashboardView';
import TeamView from './components/TeamView';
import AccessControlView from './components/AccessControlView';
import ApplicantStatusView from './components/ApplicantStatusView';
import CommunityHubView from './components/CommunityHubView';
import ClientDroneView from './components/ClientDroneView';
import ClientDashboardView from './components/ClientDashboardView';
import ClientsView from './components/ClientsView';
import AuthView from './components/AuthView';
import OnboardingTour from './components/OnboardingTour';
import DroneManagement from './components/DroneManagement';
import ServiceCatalogView from './components/ServiceCatalogView';
import CustomerIntakeForm from './components/CustomerIntakeForm';
import ScheduleView from './components/ScheduleView';
import EmployeeTrainingView from './components/EmployeeTrainingView';
import PayItForwardView from './components/PayItForwardView';
import RegisterBusinessForm from './components/RegisterBusinessForm';
import { ArborLinkApp } from './arborlink/ArborLinkApp';
import EducationView from './components/EducationView';
import LandingPage from './components/LandingPage';
// Public tools and the heavy property views are code-split: the dashboard does
// not need the worksheet forms, the 3D survey, or the public marketing pages in
// its initial bundle.
const PublicArborAI = lazy(() => import('./components/PublicArborAI'));
const PublicWorksheetz = lazy(() => import('./components/PublicWorksheetz'));
const PublicArborLink = lazy(() => import('./components/PublicArborLink'));
const PublicVox = lazy(() => import('./components/PublicVox'));
const PropertyIntelligenceView = lazy(() => import('./components/PropertyIntelligenceView'));

import { seedMissionTemplates, requestMission, updateMission as updateDroneMission, simulateFlight, generateMissionInvoice } from './services/droneService';

const MOCK_CREWS: Crew[] = [];

const INITIAL_DRONES: Drone[] = [];

const INITIAL_ACTIVITIES: Activity[] = [];

import ArbEWidget from './components/ArbEWidget';

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(() => {
    const savedUserStr = localStorage.getItem('greentree_user');
    return savedUserStr ? JSON.parse(savedUserStr) : null;
  });
  const [showTour, setShowTour] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const savedUser = localStorage.getItem('greentree_user');
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      if (parsedUser.role === 'client') return 'client-dashboard';
      if (parsedUser.role === 'applicant') return 'applicant-status';
      return 'dashboard';
    }
    return 'dashboard';
  });
  const [selectedDroneService, setSelectedDroneService] = useState<string | undefined>();
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [showGuestLoginPrompt, setShowGuestLoginPrompt] = useState(false);
  // Public tool routing. Worksheetz lives at a real path (/worksheets) so it is
  // indexable and shareable; ArborAI and ArborLink keep their hash routes.
  const resolvePublicView = (): string => {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/worksheets' || path === '/worksheetz') return 'worksheetz';
    if (path === '/vox') return 'vox';
    if (path === '/education') return 'education';
    const hash = window.location.hash.replace(/^#\/?/, '');
    if (['arborai', 'worksheetz', 'arborlink', 'vox', 'education'].includes(hash)) return hash;
    return 'landing';
  };

  const [publicView, setPublicViewInner] = useState<string>(resolvePublicView);

  const setPublicView = (view: string) => {
    if (view === 'worksheetz') {
      // Worksheetz is the published field app, served as its own static build
      // at /worksheets. A real navigation is required so the full app (auth,
      // Firestore sync, Google Workspace panel) loads instead of the old
      // in-SPA preview.
      window.location.assign('/worksheets');
      return;
    }
    if (view === 'vox') {
      // Vox gets a real path so it is shareable and indexable like Worksheetz.
      if (window.location.pathname !== '/vox') {
        history.pushState(null, '', '/vox');
      }
      setPublicViewInner('vox');
      return;
    }
    if (view === 'education') {
      if (window.location.pathname !== '/education') {
        history.pushState(null, '', '/education');
      }
      setPublicViewInner('education');
      return;
    }
    if (['arborai', 'arborlink'].includes(view)) {
      if (window.location.hash.replace(/^#\/?/, '') !== view) {
        history.pushState(null, '', '/#/' + view);
      }
      setPublicViewInner(view);
      return;
    }
    // Landing / login / other in-app views live at the site root.
    if (window.location.pathname !== '/' || window.location.hash) {
      history.pushState(null, '', '/');
    }
    setPublicViewInner(view);
  };

  // Keep the UI in sync with browser back/forward for the public tool pages.
  useEffect(() => {
    // Old bookmarks use /#/worksheetz; move them to the canonical path once.
    if (window.location.hash.replace(/^#\/?/, '') === 'worksheetz') {
      window.location.replace('/worksheets');
      return;
    }
    const syncFromLocation = () => {
      const path = window.location.pathname.replace(/\/+$/, '') || '/';
      const hash = window.location.hash.replace(/^#\/?/, '');
      if (path === '/worksheets' || path === '/worksheetz' || hash === 'worksheetz') {
        if (!/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) {
          window.location.replace('/worksheets');
          return;
        }
        setPublicViewInner('worksheetz');
        return;
      }
      if (path === '/vox' || hash === 'vox') {
        setPublicViewInner('vox');
        return;
      }
      if (path === '/education' || hash === 'education') {
        setPublicViewInner('education');
        return;
      }
      if (['arborai', 'arborlink'].includes(hash)) {
        setPublicViewInner(hash);
        return;
      }
      setPublicViewInner((prev) => (['arborai', 'worksheetz', 'arborlink', 'vox', 'education', 'login'].includes(prev) ? 'landing' : prev));
    };
    window.addEventListener('popstate', syncFromLocation);
    window.addEventListener('hashchange', syncFromLocation);
    return () => {
      window.removeEventListener('popstate', syncFromLocation);
      window.removeEventListener('hashchange', syncFromLocation);
    };
  }, []);

  const [state, dispatch] = useReducer(dataReducer, initialDataState);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [changeLogEntries, setChangeLogEntries] = useState<ChangeLogEntry[]>([]);
  const {
    leads,
    jobs,
    expenses,
    drones,
    missions,
    missionTemplates,
    flightLogs,
    missionReports,
    activities,
    contacts,
    financialRecords,
    timeEntries,
    contractorInvoices,
    ledgerEntries,
    notifications,
    activeToasts,
    arborlinkJobs,
  } = state;

  const prevMissions = useRef<DroneMission[]>([]);
  const prevConvos = useRef<string[]>([]);
  const arborlinkJobsRef = useRef<any[]>([]);

  const playSimulatedSound = () => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
      osc.frequency.setValueAtTime(880, ctx.currentTime + 0.12); // A5
      
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start();
      osc.stop(ctx.currentTime + 0.6);
    } catch (e) {
      // safe fallback
    }
  };

  const triggerNotification = (params: { title: string; message: string; type: NotificationType; meta?: any }) => {
    const id = `noti-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`;
    const newNoti: NotificationItem = {
      id,
      title: params.title,
      message: params.message,
      timestamp: new Date().toISOString(),
      type: params.type,
      read: false,
      meta: params.meta
    };

    dispatch({ type: 'ADD_NOTIFICATION', payload: newNoti });
    dispatch({ type: 'ADD_TOAST', payload: newNoti });

    playSimulatedSound();

    setTimeout(() => {
      dispatch({ type: 'REMOVE_TOAST', payload: id });
    }, 6000);
  };

  // Owner-only operations watch: one-hour appointment reminders and the live
  // site-activity feed. Lives at App level so it keeps working on every tab,
  // not just while the dashboard is open.
  const opsAlerts = useOperationsAlerts({ jobs, user, onNotify: triggerNotification });

  const handleSimulateNotification = async (type: 'APPLICATION' | 'MISSION') => {
    if (type === 'APPLICATION') {
      let jobList: any[] = [];
      try {
        const jobsSnap = await getDocs(collection(db, 'arborlink_jobs'));
        jobList = jobsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) {
        console.error(e);
      }

      let targetJobId = '';
      let targetJobTitle = 'Emergency Climber Needed';
      let targetCompanyId = 'comp-sim';

      if (jobList.length > 0) {
        const randJob = jobList[Math.floor(Math.random() * jobList.length)];
        targetJobId = randJob.id;
        targetJobTitle = randJob.title;
        targetCompanyId = randJob.companyId || 'comp-sim';
      } else {
        try {
          // idempotent seed: fixed doc id so repeated simulations never duplicate the listing
          const simJobRef = doc(db, 'arborlink_jobs', 'sim-feeler-job');
          const simPayload = {
            title: 'Feeler- Simulated High Canopy Pruning',
            description: 'Looking for a verified expert tree climber to assist with heavy canopy pruning.',
            companyId: 'comp-sim',
            companyName: 'GreenTree Arboriculture',
            date: new Date().toISOString(),
            location: 'Columbus, OH',
            payRate: '$45/hr',
            requiredSkills: ['Climber'],
            requiredCerts: ['ISA Certified Arborist'],
            status: 'OPEN',
            applicants: []
          };
          await setDoc(simJobRef, simPayload);
          targetJobId = simJobRef.id;
          targetJobTitle = 'Simulated High Canopy Pruning';
        } catch (e) {
          console.error(e);
        }
      }

      if (targetJobId) {
        const randomNames = ['Arb-E Expert', 'Climber Monkey 🐒', 'Oak Whisperer', 'Sam Green', 'Pine Climber Pete'];
        const randomName = randomNames[Math.floor(Math.random() * randomNames.length)];
        
        const mockConv = {
          jobId: targetJobId,
          workerId: `sim-worker-${Date.now()}`,
          employerId: targetCompanyId,
          messages: [
            {
              id: `msg-${Date.now()}`,
              senderId: `sim-worker-${Date.now()}`,
              text: `Hello! This is a simulated application from ${randomName} for the role of ${targetJobTitle}. We are branching out!`,
              timestamp: new Date().toISOString()
            }
          ],
          lastMessageAt: new Date().toISOString()
        };

        try {
          await addDoc(collection(db, 'arborlink_conversations'), mockConv);
          logActivity('SYSTEM', `Candidate ${randomName} applied to ${targetJobTitle}.`);
        } catch (e) {
          console.error("Failed to add mock conversation", e);
        }
      }
    } else if (type === 'MISSION') {
      let missionList: any[] = [];
      try {
        const missionsSnap = await getDocs(collection(db, 'droneMissions'));
        missionList = missionsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) {
        console.error(e);
      }

      if (missionList.length > 0) {
        const randMission = missionList[Math.floor(Math.random() * missionList.length)];
        const statuses = ['Pending Approval', 'Planned', 'In Progress', 'Completed'];
        const currentIdx = statuses.indexOf(randMission.status);
        const nextStatus = statuses[(currentIdx + 1) % statuses.length];

        try {
          await updateDoc(doc(db, 'droneMissions', randMission.id), {
            status: nextStatus
          });
        } catch (e) {
          console.error("Failed to update status", e);
        }
      } else {
        try {
          await addDoc(collection(db, 'droneMissions'), {
            jobId: 'job-sim',
            droneId: 'drone-sim',
            status: 'In Progress',
            missionType: 'Health Inspection',
            aerialMedia: [],
            customerNotes: 'Simulated survey for oak wilt detection'
          });
        } catch (e) {
          console.error("Failed to create dummy mission", e);
        }
      }
    }
  };

  const [isLoaded, setIsLoaded] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const isRemoteUpdate = useRef(false);

  // `/api/data` intentionally strips googleTokens for security. Read the
  // dedicated status endpoint so the dashboard's Gmail light reflects the
  // stored OAuth connection instead of staying grey.
  useEffect(() => {
    let cancelled = false;
    const checkGoogleConnection = async () => {
      try {
        const response = await fetch(apiUrl('/api/gmail/sync-status'));
        if (!response.ok) return;
        const status = await response.json();
        if (!cancelled && status?.success) {
          setGoogleConnected(!!status.googleConnected);
        }
      } catch {
        /* the dashboard should still render if status is temporarily unavailable */
      }
    };
    checkGoogleConnection();
    const timer = window.setInterval(checkGoogleConnection, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  // Persistence
  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch(apiUrl('/api/data'));
        if (response.ok) {
          const data = await response.json();
          isRemoteUpdate.current = true;
          const updates: Partial<DataState> = {};
          if (data.leads) updates.leads = data.leads;
          if (data.jobs) updates.jobs = data.jobs;
          if (data.expenses) updates.expenses = data.expenses;
          if (data.activities) updates.activities = data.activities;
          dispatch({ type: 'BULK_SET', payload: updates });
          if (data.googleTokens) setGoogleConnected(true);
          setTimeout(() => { isRemoteUpdate.current = false; }, 100);
        }
      } catch (error) {
        console.error('Failed to load data from server:', error);
      } finally {
        setIsLoaded(true);
      }
    };
    
    const savedUser = localStorage.getItem('greentree_user');
    if (savedUser) setUser(JSON.parse(savedUser));
    
    loadData();

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'OAUTH_AUTH_SUCCESS') {
        setGoogleConnected(true);
        // Add activity for connection
        const newActivity: Activity = {
          id: `a-${Date.now()}`,
          type: 'SYSTEM',
          message: 'Google Workspace successfully connected.',
          timestamp: new Date().toISOString(),
          user: user?.name || 'Admin'
        };
        dispatch({ type: 'ADD_ACTIVITY', payload: newActivity });
      }
    };
    window.addEventListener('message', handleMessage);

    // Socket connection
    // Firebase Hosting does not proxy WebSocket upgrades, so the default
    // websocket-first attempt fails on every connect and spams the console.
    // Polling works reliably through Hosting — use it directly.
    socketRef.current = io(API_BASE || undefined, { transports: ['polling'], upgrade: false });
    
    socketRef.current.on('data_updated', (data) => {
      isRemoteUpdate.current = true;
      const updates: Partial<DataState> = {};
      if (data.leads) updates.leads = data.leads;
      if (data.jobs) updates.jobs = data.jobs;
      if (data.expenses) updates.expenses = data.expenses;
      if (data.activities) updates.activities = data.activities;
      dispatch({ type: 'BULK_SET', payload: updates });
      setTimeout(() => { isRemoteUpdate.current = false; }, 100);
    });

    // Complete any pending redirect sign-in (the popup flow's fallback path).
    // Without this, a redirect sign-in never finishes and the app bounces back to login.
    getRedirectResult(auth).then((res) => {
      if (res?.user) {
        console.log('Redirect sign-in completed for:', res.user.email);
        beacon('redirect-signin-completed', { message: String(res.user.email || '') });
      }
    }).catch((err) => {
      beacon('redirect-signin-failed', err);
      console.error('Redirect sign-in failed:', err);
    });

    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      setIsAuthReady(true);
      const savedUserStr = localStorage.getItem('greentree_user');
      const savedUser = savedUserStr ? JSON.parse(savedUserStr) : null;
      const isMock = savedUser?.id?.startsWith('dev-') || savedUser?.id?.startsWith('guest-') || user?.id?.startsWith('dev-') || user?.id?.startsWith('guest-');

      if (!firebaseUser) {
        if (!isMock) {
          setUser(null);
          localStorage.removeItem('greentree_user');
        }
      } else {
        // Firebase user is logged in. Synchronize/restore their custom user profile.
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        getDoc(userDocRef).then((snap) => {
          if (snap.exists()) {
            const profileData = snap.data() as User;
            console.log("User role verified:", profileData.role, "Staff role:", profileData.staffRole);
            setUser(profileData);
            localStorage.setItem('greentree_user', JSON.stringify(profileData));
          } else {
            // No profile yet. Never infer staff status from email substrings.
            // Exact owner addresses get the admin bootstrap; everyone else
            // starts as a client and must be promoted from Users & Access.
            const emailStr = (firebaseUser.email || '').toLowerCase();
            const isOwner = emailStr === 'mrgreen@greentreearboriculture.com'
              || emailStr === 'brandtmgreen@gmail.com';
            const profile: User = {
              id: firebaseUser.uid,
              name: firebaseUser.displayName || 'Arbor Tree Fanatic',
              role: isOwner ? 'staff' : 'client',
              accountType: isOwner ? 'admin' : 'client',
              email: emailStr,
              address: '',
            };
            if (isOwner) profile.staffRole = 'admin';

            const profileDoc: Record<string, any> = {
              ...profile,
              createdAt: new Date().toISOString(),
            };
            if (firebaseUser.photoURL) profileDoc.photoURL = firebaseUser.photoURL;

            setDoc(userDocRef, profileDoc).then(() => {
              setUser(profile);
              localStorage.setItem('greentree_user', JSON.stringify(profile));
            }).catch((err) => {
              beacon('profile-save-failed', err);
              console.error("Failed to save auto-created profile:", err);
            });
          }
        }).catch((err) => {
          beacon('auth-restore-getdoc-failed', err);
          console.error("Failed to fetch user profile during auth restore:", err);
        });
      }
    });

    return () => {
      window.removeEventListener('message', handleMessage);
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      unsubscribeAuth();
    };
  }, []);

  const handleConnectGoogle = async () => {
    try {
      const response = await fetch(apiUrl('/api/auth/google/url'));
      const { url } = await response.json();
      
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      const popup = window.open(url, 'google_auth', `width=${width},height=${height},top=${top},left=${left}`);
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        // Fallback for blocked popups
        window.location.href = url;
      }
    } catch (error) {
      console.error('Failed to get Google auth URL:', error);
    }
  };

  const importContacts = async () => {
    try {
      const response = await authFetch('/api/workspace/contacts');
      if (!response.ok) throw new Error('Failed to fetch contacts');
      const data = await response.json();
      if (data.success && data.contacts) {
        let newCount = 0;
        for (const contact of data.contacts) {
          // Check if contact already exists
          const exists = contacts.find(c => c.email === contact.email);
          if (!exists && contact.email) {
            await addDoc(collection(db, 'contacts'), sanitizeForFirestore({
              name: contact.name,
              email: contact.email,
              phone: contact.phone,
              address: contact.address,
              // Google address book imports everyone the owner knows - staff, contractors,
              // suppliers, family. Tagging them OTHER keeps them out of the customer list
              // until they are deliberately reclassified as customers.
              personType: 'OTHER',
              createdAt: new Date().toISOString(),
              userId: user?.name || 'System'
            }));
            newCount++;
          }
        }
        logActivity('SYSTEM', `Imported ${data.contacts.length} contacts from Google Workspace. (${newCount} new saved to database)`);
        triggerNotification({
          title: '👥 Contacts Sync Successful',
          message: `Arb-E successfully imported ${data.contacts.length} Workspace contacts. ${newCount} new clients saved directly to database!`,
          type: 'SYSTEM'
        });
      }
    } catch (error) {
      console.error('Failed to import contacts:', error);
      triggerNotification({
        title: '⚠️ Workspace Sync Error',
        message: 'Could not fetch Contacts. Please verify Google Workspace is fully authorized.',
        type: 'SYSTEM'
      });
    }
  };

  useEffect(() => {
    if (!isAuthReady || !auth.currentUser) return;

    if (user?.staffRole === 'admin') {
      seedMissionTemplates().catch(console.error);
    }

    // Secure operational subscriptions
    console.log('Attaching Firestore listeners...');
    const unsubJobs = onSnapshot(collection(db, 'jobs'), (snapshot) => {
      dispatch({ type: 'SET_JOBS', payload: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Job)) });
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'jobs'));

    const unsubLeads = onSnapshot(collection(db, 'leads'), (snapshot) => {
      dispatch({ type: 'SET_LEADS', payload: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Lead)) });
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'leads'));

    // Staff-only or conditional subscriptions
    let unsubExpenses = () => {};
    if (user?.role === 'staff') {
      unsubExpenses = onSnapshot(collection(db, 'expenses'), (snapshot) => {
        dispatch({ type: 'SET_EXPENSES', payload: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Expense)) });
      }, (error) => handleFirestoreError(error, OperationType.LIST, 'expenses'));
    } else {
      dispatch({ type: 'SET_EXPENSES', payload: [] });
    }

    let unsubDrones = () => {};
    if (user?.role === 'staff') {
      unsubDrones = onSnapshot(collection(db, 'drones'), (snapshot) => {
        dispatch({ type: 'SET_DRONES', payload: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Drone)) });
      }, (error) => handleFirestoreError(error, OperationType.LIST, 'drones'));
    } else {
      dispatch({ type: 'SET_DRONES', payload: [] });
    }

    const unsubMissions = onSnapshot(collection(db, 'droneMissions'), (snapshot) => {
      const currentMissions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DroneMission));
      if (prevMissions.current.length > 0) {
        currentMissions.forEach(m => {
          const old = prevMissions.current.find(pm => pm.id === m.id);
          if (old && old.status !== m.status) {
            triggerNotification({
              title: 'Drone Mission Update',
              message: `Arborist Drone Mission "${m.missionType}" is now: ${m.status}.`,
              type: 'MISSION',
              meta: { id: m.id }
            });
          }
        });
      }
      prevMissions.current = currentMissions;
      dispatch({ type: 'SET_MISSIONS', payload: currentMissions });
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'droneMissions'));

    const unsubArborLinkJobs = onSnapshot(collection(db, 'arborlink_jobs'), (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      arborlinkJobsRef.current = data;
      dispatch({ type: 'SET_ARBORLINK_JOBS', payload: data });
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'arborlink_jobs'));

    const unsubArborLinkConversations = onSnapshot(collection(db, 'arborlink_conversations'), (snapshot) => {
      const currentConvos = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
      const currentIds = currentConvos.map(c => c.id);
      
      if (prevConvos.current.length > 0) {
        currentConvos.forEach(c => {
          if (!prevConvos.current.includes(c.id)) {
            const associatedJob = arborlinkJobsRef.current.find(job => job.id === c.jobId);
            const jobTitle = associatedJob?.title || 'ArborLink Position';
            triggerNotification({
              title: 'New Job Application Received 💼',
              message: `A new candidate applied for the position: "${jobTitle}".`,
              type: 'APPLICATION',
              meta: { jobId: c.jobId }
            });
          }
        });
      }
      prevConvos.current = currentIds;
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'arborlink_conversations'));

    const unsubTemplates = onSnapshot(collection(db, 'missionTemplates'), (snapshot) => {
      dispatch({ type: 'SET_MISSION_TEMPLATES', payload: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MissionTemplate)) });
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'missionTemplates'));

    let unsubLogs = () => {};
    if (user?.role === 'staff') {
      unsubLogs = onSnapshot(collection(db, 'flightLogs'), (snapshot) => {
        dispatch({ type: 'SET_FLIGHT_LOGS', payload: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FlightLog)) });
      }, (error) => handleFirestoreError(error, OperationType.LIST, 'flightLogs'));
    } else {
      dispatch({ type: 'SET_FLIGHT_LOGS', payload: [] });
    }

    const unsubReports = onSnapshot(collection(db, 'missionReports'), (snapshot) => {
      dispatch({ type: 'SET_MISSION_REPORTS', payload: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MissionReport)) });
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'missionReports'));

    let unsubscribeContacts = () => {};
    if (user?.role === 'staff') {
      unsubscribeContacts = onSnapshot(collection(db, 'contacts'), (snapshot) => {
        const contactsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Contact));
        dispatch({ type: 'SET_CONTACTS', payload: contactsData });
      }, (error) => {
        handleFirestoreError(error, OperationType.GET, 'contacts');
      });
    } else {
      dispatch({ type: 'SET_CONTACTS', payload: [] });
    }

    let unsubscribeFinancials = () => {};
    if (user?.role === 'staff') {
      unsubscribeFinancials = onSnapshot(collection(db, 'financialRecords'), (snapshot) => {
        const financialsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FinancialRecord));
        dispatch({ type: 'SET_FINANCIAL_RECORDS', payload: financialsData });
      }, (error) => {
        handleFirestoreError(error, OperationType.GET, 'financialRecords');
      });
    } else {
      dispatch({ type: 'SET_FINANCIAL_RECORDS', payload: [] });
    }

    let unsubscribeTimeEntries = () => {};
    if (user?.role === 'staff') {
      unsubscribeTimeEntries = onSnapshot(collection(db, 'timeEntries'), (snapshot) => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as TimeEntry));
        dispatch({ type: 'SET_TIME_ENTRIES', payload: data });
      }, (error) => handleFirestoreError(error, OperationType.GET, 'timeEntries'));
    } else {
      dispatch({ type: 'SET_TIME_ENTRIES', payload: [] });
    }

    let unsubscribeInvoices = () => {};
    if (user?.role === 'staff') {
      unsubscribeInvoices = onSnapshot(collection(db, 'contractorInvoices'), (snapshot) => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ContractorInvoice));
        dispatch({ type: 'SET_CONTRACTOR_INVOICES', payload: data });
      }, (error) => handleFirestoreError(error, OperationType.GET, 'contractorInvoices'));
    } else {
      dispatch({ type: 'SET_CONTRACTOR_INVOICES', payload: [] });
    }

    let unsubscribeLedger = () => {};
    if (user?.role === 'staff') {
      unsubscribeLedger = onSnapshot(collection(db, 'ledger'), (snapshot) => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as LedgerEntry));
        dispatch({ type: 'SET_LEDGER_ENTRIES', payload: data });
      }, (error) => handleFirestoreError(error, OperationType.GET, 'ledger'));
    } else {
      dispatch({ type: 'SET_LEDGER_ENTRIES', payload: [] });
    }

    // Inventory & change ledger (additive integration 2026-09-11)
    const unsubInventory = onSnapshot(collection(db, 'inventory'), (snapshot) => {
      setInventory(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem)));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'inventory'));

    let unsubChangeLog = () => {};
    if (user?.role === 'staff') {
      unsubChangeLog = onSnapshot(
        query(collection(db, 'changeLog'), orderBy('at', 'desc'), limit(200)),
        (snapshot) => {
          setChangeLogEntries(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ChangeLogEntry)));
        },
        (error) => handleFirestoreError(error, OperationType.LIST, 'changeLog'));
    }

    return () => {
      unsubJobs();
      unsubLeads();
      unsubExpenses();
      unsubDrones();
      unsubMissions();
      unsubArborLinkJobs();
      unsubArborLinkConversations();
      unsubTemplates();
      unsubLogs();
      unsubReports();
      unsubscribeContacts();
      unsubscribeFinancials();
      unsubscribeTimeEntries();
      unsubscribeInvoices();
      unsubscribeLedger();
      unsubInventory();
      unsubChangeLog();
    };
  }, [isAuthReady, user]);

  useEffect(() => {
    if (user) localStorage.setItem('greentree_user', JSON.stringify(user));
    
    if (isLoaded && socketRef.current && !isRemoteUpdate.current) {
      const data = {
        leads,
        jobs,
        expenses,
        drones,
        missions,
        activities
      };

      const payloadSize = JSON.stringify(data).length;
      if (payloadSize > 15_000_000) {
        console.warn(`Skipping socket sync: payload is ${payloadSize} bytes. Firestore remains the source of truth.`);
      } else {
        socketRef.current.emit('update_data', data);
      }
    }
  }, [user, leads, jobs, expenses, drones, missions, activities, isLoaded]);

  const logActivity = (type: Activity['type'], message: string, authorName?: string) => {
    const newActivity: Activity = {
      id: Math.random().toString(36).substr(2, 9),
      type,
      message,
      timestamp: new Date().toISOString(),
      user: authorName || user?.name || 'System'
    };
    dispatch({ type: 'ADD_ACTIVITY', payload: newActivity });
  };

  const handleLogin = (newUser: User) => {
    setUser(newUser);
    logActivity('SYSTEM', `${newUser.name} logged in as ${newUser.role}`, newUser.name);
    if (newUser.role === 'client' || newUser.role === 'guest') {
      if (newUser.role === 'client') setShowTour(true);
      setActiveTab('client-dashboard');
    } else if (newUser.role === 'applicant') {
      setActiveTab('applicant-status');
    } else if (newUser.staffRole === 'employee' || newUser.staffRole === 'contractor') {
      setActiveTab('crew');
    } else {
      setActiveTab('dashboard');
    }
  };

  const handleLogout = async () => {
    logActivity('SYSTEM', `${user?.name} logged out`);
    localStorage.removeItem('greentree_user');
    setUser(null);
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  const requireAuth = (action?: () => void): boolean => {
    if (user?.role === 'guest') {
      setShowGuestLoginPrompt(true);
      return false;
    } else {
      if (action) action();
      return true;
    }
  };

  const addLead = async (lead: Lead) => {
    return requireAuth(async () => {
      try {
        const ref = await addDoc(collection(db, 'leads'), sanitizeForFirestore(lead));
        logActivity('LEAD', `New opportunity: ${lead.clientName} at ${lead.address}`);
        logChange({ who: user?.name || 'System', whoRole: user?.role === 'staff' ? user.staffRole : user?.role, action: 'CREATE', entity: 'lead', entityId: ref.id, entityLabel: lead.clientName, summary: `Lead created for ${lead.clientName} (${lead.serviceType})` });
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'leads');
      }
    });
  };

  const updateLead = async (updatedLead: Lead) => {
    requireAuth(async () => {
      try {
        const oldLead = leads.find(l => l.id === updatedLead.id);
        const leadData = { ...updatedLead };
        delete (leadData as any).id;
        const leadRef = doc(db, 'leads', updatedLead.id);
        try {
          await updateDoc(leadRef, sanitizeForFirestore(leadData));
        } catch (err: any) {
          // The doc can be missing (purged server-side, or stale local state).
          // Upsert instead of failing so the dashboard stays consistent.
          if (String(err?.message || '').includes('No document to update')) {
            await setDoc(leadRef, sanitizeForFirestore(leadData), { merge: true });
          } else {
            throw err;
          }
        }
        const changedFields = oldLead ? (Object.keys(updatedLead) as (keyof Lead)[]).filter(k => (oldLead as any)[k] !== (updatedLead as any)[k] && k !== 'id').map(String) : [];
        logChange({ who: user?.name || 'System', whoRole: user?.role === 'staff' ? user.staffRole : user?.role, action: 'UPDATE', entity: 'lead', entityId: updatedLead.id, entityLabel: updatedLead.clientName, summary: `Lead updated: ${updatedLead.clientName}`, changedFields });
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'leads');
      }
    });
  };

  const deleteLead = async (leadId: string): Promise<{ ok: boolean; error?: string }> => {
    if (user?.role === 'guest') {
      setShowGuestLoginPrompt(true);
      return { ok: false, error: 'Sign in to delete leads.' };
    }
    if (!auth.currentUser) {
      return { ok: false, error: 'Your session has expired. Please sign in again.' };
    }
    try {
      await auth.currentUser.getIdToken(true);
      const lead = leads.find(l => l.id === leadId);
      // Soft delete: keep the record for audit, mark it deleted.
      try {
        await updateDoc(doc(db, 'leads', leadId), sanitizeForFirestore({ isDeleted: true, deletedAt: new Date().toISOString(), deletedBy: user?.name || 'System' }));
      } catch (error) {
        // The lead may have been converted or already deleted elsewhere. Its
        // absence is the desired end state, so do not show a "file not found"
        // style error to the owner.
        if (!isMissingDocumentError(error)) throw error;
      }
      logChange({ who: user?.name || 'System', whoRole: user?.role === 'staff' ? user.staffRole : user?.role, action: 'DELETE', entity: 'lead', entityId: leadId, entityLabel: lead?.clientName || '', summary: `Lead deleted: ${lead?.clientName || leadId}` });
      logActivity('LEAD', `Lead removed: ${lead?.clientName || leadId}`);
      return { ok: true };
    } catch (error: any) {
      console.error('Delete lead failed:', error);
      const message = error?.code === 'permission-denied'
        ? 'Delete was blocked: your session expired or you do not have permission. Sign in again and retry.'
        : (error?.message || 'Delete failed. Please try again.');
      return { ok: false, error: message };
    }
  };
  
  /**
   * Step 1 of the estimate workflow: the customer has asked for a quote, so book the site
   * visit. Carries the request details onto the job (name, phone, email, address), sets the
   * visit date so it appears on the calendar, and parks the job in ESTIMATING until priced.
   */
  const scheduleEstimate = async (
    lead: Lead,
    schedule: { date: string; time: string; durationMins: number; notes: string }
  ) => {
    requireAuth(async () => {
      try {
        const newJobData: any = {
          ...lead,
          status: JobStatus.ESTIMATING,
          scheduledDate: `${schedule.date}T${schedule.time}`,
          estimatedDurationMins: schedule.durationMins,
          estimateNotes: schedule.notes || '',
          estimateScheduledAt: new Date().toISOString(),
          inventory: [],
          lineItems: [],
          totalPrice: 0,
          safetyChecked: false,
          documents: []
        };
        delete newJobData.id;

        const jobRef = await addDoc(collection(db, 'jobs'), sanitizeForFirestore(newJobData));
        await updateDoc(doc(db, 'leads', lead.id), sanitizeForFirestore({
          isDeleted: true,
          deletedAt: new Date().toISOString(),
          deletedBy: user?.name || 'System',
          convertedToJobId: jobRef.id
        }));

        logChange({
          who: user?.name || 'System',
          whoRole: user?.role === 'staff' ? user.staffRole : user?.role,
          action: 'CREATE',
          entity: 'job',
          entityId: jobRef.id,
          entityLabel: lead.clientName,
          summary: `Estimate visit scheduled for ${schedule.date} ${schedule.time}`
        });
        logActivity('JOB', `Estimate visit scheduled: ${lead.clientName} (${schedule.date} ${schedule.time})`);
        setActiveTab('schedule');
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'jobs');
      }
    });
  };

  const convertLeadToJob = async (leadId: string) => {
    requireAuth(async () => {
      const lead = leads.find(l => l.id === leadId);
    if (lead) {
      try {
        const newJobData = {
          ...lead,
          status: JobStatus.ESTIMATING,
          inventory: [],
          lineItems: [],
          totalPrice: 0,
          safetyChecked: false,
          documents: []
        };
        // Remove the old ID to let Firestore generate one, or use it
        delete (newJobData as any).id;
        
        const jobRef = await addDoc(collection(db, 'jobs'), sanitizeForFirestore(newJobData));
        await updateDoc(doc(db, 'leads', leadId), sanitizeForFirestore({ isDeleted: true, deletedAt: new Date().toISOString(), deletedBy: user?.name || 'System', convertedToJobId: jobRef.id }));
        logChange({ who: user?.name || 'System', whoRole: user?.role === 'staff' ? user.staffRole : user?.role, action: 'UPDATE', entity: 'lead', entityId: leadId, entityLabel: lead.clientName, summary: `Lead converted to job ${jobRef.id}`, changedFields: ['isDeleted', 'convertedToJobId'] });
        
        logActivity('JOB', `Lead converted to job pipeline: ${lead.clientName}`);
        setActiveTab('jobs');

        let contactId = '';
        const existingContact = contacts.find(c => c.email === lead.email);
        if (existingContact) {
          contactId = existingContact.id;
        } else {
          const newContactRef = await addDoc(collection(db, 'contacts'), sanitizeForFirestore({
            name: lead.clientName,
            email: lead.email,
            phone: lead.phone,
            address: lead.address,
            createdAt: new Date().toISOString(),
            userId: user?.name || 'System'
          }));
          contactId = newContactRef.id;
        }

        await addDoc(collection(db, 'financialRecords'), sanitizeForFirestore({
          type: 'ESTIMATE',
          status: 'DRAFT',
          contactId,
          jobId: jobRef.id,
          amount: 0,
          date: new Date().toISOString(),
          lineItems: [],
          createdAt: new Date().toISOString()
        }));
        logActivity('FINANCE', `Automated Estimate generated for ${lead.clientName}`);
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'convert_lead');
      }
    }
    });
  };

  const addJob = async (job: Job) => {
    requireAuth(async () => {
      try {
      const jobRef = await addDoc(collection(db, 'jobs'), sanitizeForFirestore(job));
      const newJob = { ...job, id: jobRef.id };
      logActivity('JOB', `Direct job created: ${newJob.clientName} at ${newJob.address}`);
      logChange({ who: user?.name || 'System', whoRole: user?.role === 'staff' ? user.staffRole : user?.role, action: 'CREATE', entity: 'job', entityId: jobRef.id, entityLabel: newJob.clientName, summary: `Job created for ${newJob.clientName}` });
      
      let contactId = '';
      const existingContact = contacts.find(c => c.email === newJob.email || c.name === newJob.clientName);
      if (existingContact) {
        contactId = existingContact.id;
      } else {
        const newContactRef = await addDoc(collection(db, 'contacts'), sanitizeForFirestore({
          name: newJob.clientName,
          email: newJob.email || '',
          phone: newJob.phone || '',
          address: newJob.address,
          createdAt: new Date().toISOString(),
          userId: user?.name || 'System'
        }));
        contactId = newContactRef.id;
      }

      await addDoc(collection(db, 'financialRecords'), sanitizeForFirestore({
        type: 'ESTIMATE',
        status: 'DRAFT',
        contactId,
        jobId: newJob.id,
        amount: 0,
        date: new Date().toISOString(),
        lineItems: [],
        createdAt: new Date().toISOString()
      }));
      logActivity('FINANCE', `Automated Estimate generated for ${newJob.clientName}`);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'jobs');
    }
    });
  };

  const updateJob = async (updatedJob: Job) => {
    requireAuth(async () => {
      try {
        const oldJob = jobs.find(j => j.id === updatedJob.id);
        
        const jobData = { ...updatedJob };
        const jobId = jobData.id;
        delete (jobData as any).id;

        await updateDoc(doc(db, 'jobs', jobId), sanitizeForFirestore(jobData));

        const changedFields = oldJob ? (Object.keys(updatedJob) as (keyof Job)[]).filter(k => (oldJob as any)[k] !== (updatedJob as any)[k] && k !== 'id').map(String) : [];
        if (changedFields.length > 0) {
          logChange({ who: user?.name || 'System', whoRole: user?.role === 'staff' ? user.staffRole : user?.role, action: 'UPDATE', entity: 'job', entityId: jobId, entityLabel: updatedJob.clientName, summary: `Job updated: ${updatedJob.clientName} (${changedFields.slice(0, 4).join(', ')}${changedFields.length > 4 ? '…' : ''})`, changedFields });
        }

        if (oldJob && oldJob.status !== updatedJob.status) {
          logActivity('JOB', `Job status updated: ${updatedJob.clientName} is now ${updatedJob.status}`);

          if (updatedJob.status === JobStatus.COMPLETED || updatedJob.status === JobStatus.INVOICED || updatedJob.status === JobStatus.PAID) {
            let contactId = '';
            const existingContact = contacts.find(c => c.email === updatedJob.email);
            if (existingContact) {
              contactId = existingContact.id;
            } else {
              const newContactRef = await addDoc(collection(db, 'contacts'), sanitizeForFirestore({
                name: updatedJob.clientName,
                email: updatedJob.email,
                phone: updatedJob.phone,
                address: updatedJob.address,
                createdAt: new Date().toISOString(),
                userId: user?.name || 'System'
              }));
              contactId = newContactRef.id;
            }

            const existingInvoice = financialRecords.find(f => f.jobId === updatedJob.id && f.type === 'INVOICE');
            if (!existingInvoice) {
              await addDoc(collection(db, 'financialRecords'), sanitizeForFirestore({
                type: 'INVOICE',
                status: updatedJob.status === JobStatus.INVOICED ? 'SENT' : 'DRAFT',
                contactId,
                jobId: updatedJob.id,
                amount: updatedJob.totalPrice || 0,
                date: new Date().toISOString(),
                dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                lineItems: updatedJob.lineItems || [],
                createdAt: new Date().toISOString()
              }));
              logActivity('FINANCE', `Automated Invoice generated for ${updatedJob.clientName}`);
            } else if (updatedJob.status === JobStatus.INVOICED && existingInvoice.status === 'DRAFT') {
              await updateDoc(doc(db, 'financialRecords', existingInvoice.id), sanitizeForFirestore({
                status: 'SENT',
                amount: updatedJob.totalPrice || 0,
                lineItems: updatedJob.lineItems || []
              }));
              logActivity('FINANCE', `Invoice sent for ${updatedJob.clientName}`);
            } else if (updatedJob.status === JobStatus.PAID && existingInvoice.status !== 'PAID') {
              await updateDoc(doc(db, 'financialRecords', existingInvoice.id), sanitizeForFirestore({
                status: 'PAID'
              }));
              logActivity('FINANCE', `Invoice paid for ${updatedJob.clientName}`);
            }
          }
        }
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'firebaseUpdateJob');
      }
    });
  };

  const deleteJob = async (jobId: string): Promise<{ ok: boolean; error?: string }> => {
    if (user?.role === 'guest') {
      setShowGuestLoginPrompt(true);
      return { ok: false, error: 'Sign in to delete jobs.' };
    }
    // A stale localStorage profile can keep the dashboard visible after the
    // Firebase session has expired. Fail loudly instead of silently doing nothing.
    if (!auth.currentUser) {
      return { ok: false, error: 'Your session has expired. Please sign in again.' };
    }
    try {
      // Force a fresh ID token; a stale tab can otherwise get a silent
      // permission-denied from Firestore.
      await auth.currentUser.getIdToken(true);
      const job = jobs.find(j => j.id === jobId);
      if (!job) {
        return { ok: false, error: 'That job is no longer in the pipeline.' };
      }
      const linkedInvoices = financialRecords.filter(f => f.jobId === jobId);
      if ((linkedInvoices || []).length > 0) {
        alert(`This job has ${linkedInvoices.length} linked invoice/quote record(s). The job will be soft-deleted, but its financial records stay for accounting. Delete them separately in Financials → Records if needed.`);
      }
      // Soft delete keeps history auditable.
      try {
        await updateDoc(doc(db, 'jobs', jobId), sanitizeForFirestore({ isDeleted: true, deletedAt: new Date().toISOString(), deletedBy: user?.name || 'System' }));
      } catch (error) {
        if (!isMissingDocumentError(error)) throw error;
      }
      logChange({ who: user?.name || 'System', whoRole: user?.role === 'staff' ? user.staffRole : user?.role, action: 'DELETE', entity: 'job', entityId: jobId, entityLabel: job?.clientName || '', summary: `Job deleted: ${job?.clientName || jobId}` });
      logActivity('JOB', `Job removed: ${job?.clientName || jobId}`);
      return { ok: true };
    } catch (error: any) {
      console.error('Delete job failed:', error);
      const message = error?.code === 'permission-denied'
        ? 'Delete was blocked: your session has expired or you do not have permission. Sign in again and retry.'
        : (error?.message || 'Delete failed. Please try again.');
      return { ok: false, error: message };
    }
  };

  // --- Inventory (additive 2026-09-11) ---
  const addInventoryItem = async (item: InventoryItem) => {
    requireAuth(async () => {
      try {
        const { id, ...data } = item;
        const ref = await addDoc(collection(db, 'inventory'), sanitizeForFirestore(data));
        logChange({ who: user?.name || 'System', whoRole: user?.role === 'staff' ? user.staffRole : user?.role, action: 'CREATE', entity: 'inventory', entityId: ref.id, entityLabel: item.name, summary: `Inventory item added: ${item.name} (${item.quantity} ${item.unit})` });
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'inventory');
      }
    });
  };

  const updateInventoryItem = async (item: InventoryItem) => {
    requireAuth(async () => {
      try {
        const oldItem = inventory.find(i => i.id === item.id);
        const { id, ...data } = item;
        await updateDoc(doc(db, 'inventory', item.id), sanitizeForFirestore(data));
        const changedFields = oldItem ? (Object.keys(item) as (keyof InventoryItem)[]).filter(k => (oldItem as any)[k] !== (item as any)[k] && k !== 'id').map(String) : [];
        logChange({ who: user?.name || 'System', whoRole: user?.role === 'staff' ? user.staffRole : user?.role, action: 'UPDATE', entity: 'inventory', entityId: item.id, entityLabel: item.name, summary: `Inventory item updated: ${item.name}`, changedFields });
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'inventory');
      }
    });
  };

  const deleteInventoryItem = async (itemId: string) => {
    requireAuth(async () => {
      try {
        const item = inventory.find(i => i.id === itemId);
        try {
          await updateDoc(doc(db, 'inventory', itemId), sanitizeForFirestore({ isDeleted: true, deletedAt: new Date().toISOString(), deletedBy: user?.name || 'System' }));
        } catch (error) {
          if (!isMissingDocumentError(error)) throw error;
        }
        logChange({ who: user?.name || 'System', whoRole: user?.role === 'staff' ? user.staffRole : user?.role, action: 'DELETE', entity: 'inventory', entityId: itemId, entityLabel: item?.name || '', summary: `Inventory item deleted: ${item?.name || itemId}` });
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'inventory');
      }
    });
  };

  // --- Financial records (quotes/invoices) delete + restore (additive 2026-09-11) ---
  const deleteFinancialRecord = async (recordId: string) => {
    requireAuth(async () => {
      try {
        const record = financialRecords.find(f => f.id === recordId);
        try {
          await updateDoc(doc(db, 'financialRecords', recordId), sanitizeForFirestore({ isDeleted: true, deletedAt: new Date().toISOString(), deletedBy: user?.name || 'System' }));
        } catch (error) {
          if (!isMissingDocumentError(error)) throw error;
        }
        logChange({ who: user?.name || 'System', whoRole: user?.role === 'staff' ? user.staffRole : user?.role, action: 'DELETE', entity: record?.type === 'INVOICE' ? 'invoice' : 'quote', entityId: recordId, entityLabel: record ? `#${record.id.slice(0, 4)} $${record.amount}` : '', summary: `${record?.type || 'Record'} deleted (soft)` });
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'financialRecords');
      }
    });
  };

  const updateFinancialRecord = async (record: FinancialRecord) => {
    requireAuth(async () => {
      try {
        const old = financialRecords.find(f => f.id === record.id);
        const { id, ...data } = record;
        await updateDoc(doc(db, 'financialRecords', record.id), sanitizeForFirestore(data));
        const changedFields = old ? (Object.keys(record) as (keyof FinancialRecord)[]).filter(k => (old as any)[k] !== (record as any)[k] && k !== 'id').map(String) : [];
        logChange({ who: user?.name || 'System', whoRole: user?.role === 'staff' ? user.staffRole : user?.role, action: 'UPDATE', entity: record.type === 'INVOICE' ? 'invoice' : 'quote', entityId: record.id, entityLabel: `#${record.id.slice(0, 4)}`, summary: `${record.type === 'INVOICE' ? 'Invoice' : 'Quote'} updated`, changedFields });
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'financialRecords');
      }
    });
  };

  const addExpense = async (expense: Expense) => {
    requireAuth(async () => {
      try {
        const expenseData = { ...expense };
        delete (expenseData as any).id;
        await addDoc(collection(db, 'expenses'), sanitizeForFirestore(expenseData));
        logActivity('FINANCE', `New expense logged: $${expense.amount} to ${expense.merchant}`);
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'expenses');
      }
    });
  };

  const handleAddTimeEntry = async (entry: TimeEntry) => {
    requireAuth(async () => {
      try {
        await setDoc(doc(db, 'timeEntries', entry.id), sanitizeForFirestore(entry));
        logActivity('FINANCE', `Time logged: ${entry.hours} hours`);
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'timeEntries');
      }
    });
  };

  const handleUpdateTimeEntry = async (entry: TimeEntry) => {
    requireAuth(async () => {
      try {
        await updateDoc(doc(db, 'timeEntries', entry.id), sanitizeForFirestore({ ...entry }));
        logActivity('FINANCE', `Time entry updated: ${entry.status}`);
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'timeEntries');
      }
    });
  };

  const handleAddContractorInvoice = async (invoice: ContractorInvoice) => {
    requireAuth(async () => {
      try {
        await setDoc(doc(db, 'contractorInvoices', invoice.id), sanitizeForFirestore(invoice));
        logActivity('FINANCE', `Contractor invoice submitted: $${invoice.amount}`);
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'contractorInvoices');
      }
    });
  };

  const handleUpdateContractorInvoice = async (invoice: ContractorInvoice) => {
    requireAuth(async () => {
      try {
        await updateDoc(doc(db, 'contractorInvoices', invoice.id), sanitizeForFirestore({ ...invoice }));
        logActivity('FINANCE', `Contractor invoice updated: ${invoice.status}`);
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'contractorInvoices');
      }
    });
  };

  const requestDroneMission = (jobId: string) => {
    requireAuth(() => {
      const job = jobs.find(j => j.id === jobId);
      const mission: DroneMission = {
        id: Math.random().toString(36).substr(2, 9),
        jobId,
        droneId: drones[0]?.id || 'unknown',
        status: 'Planned',
        missionType: 'Site Survey',
        aerialMedia: []
      };
      dispatch({ type: 'SET_MISSIONS', payload: [...missions, mission] });
      logActivity('DRONE', `Aerial mission scheduled for ${job?.clientName || 'Project'}`);
      setActiveTab('drones');
    });
  };

  // Tool pages (Vox, ArborAI, Worksheetz, ArborLink) are reachable both logged out (public
  // marketing pages) and logged in (staff opening the tools from the dashboard menu).
  // These checks used to sit inside the `!user` branch, which meant a signed-in user was
  // bounced straight into the dashboard instead of the tool they asked for.
  const toolBack = () => setPublicView('landing');
  const toolGetStarted = () => setPublicView(user ? 'landing' : 'login');
  const toolFallback = (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 mx-auto rounded-full border-4 border-stone-200 border-t-gecko animate-spin" />
        <p className="mt-4 text-xs font-black uppercase tracking-widest text-stone-400">Loading tool…</p>
      </div>
    </div>
  );

  if (publicView === 'arborai') {
    return (
      <Suspense fallback={toolFallback}>
        <PublicArborAI
          onBack={toolBack}
          onGetStarted={toolGetStarted}
          onNavigate={(v) => setPublicView(v)}
        />
      </Suspense>
    );
  }
  if (publicView === 'worksheetz') {
    return (
      <Suspense fallback={toolFallback}>
        <PublicWorksheetz
          onBack={toolBack}
          onGetStarted={toolGetStarted}
          onNavigate={(v) => setPublicView(v)}
        />
      </Suspense>
    );
  }
  if (publicView === 'arborlink') {
    return (
      <Suspense fallback={toolFallback}>
        <PublicArborLink
          onBack={toolBack}
          onGetStarted={toolGetStarted}
          onNavigate={(v) => setPublicView(v)}
        />
      </Suspense>
    );
  }
  if (publicView === 'vox') {
    return (
      <Suspense fallback={toolFallback}>
        <PublicVox
          onBack={toolBack}
          onGetStarted={toolGetStarted}
          onNavigate={(v) => setPublicView(v)}
        />
      </Suspense>
    );
  }
  if (publicView === 'education') {
    return (
      <EducationView
        publicMode
        onBack={() => setPublicView('landing')}
        onNavigate={(v) => setPublicView(v)}
      />
    );
  }

  if (!user) {
    if (publicView === 'login') {
      return (
        <div className="min-h-screen bg-stone-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <button
              onClick={() => setPublicView('landing')}
              className="flex items-center space-x-2 text-sm font-bold text-stone-500 hover:text-palm transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back to Home</span>
            </button>
          </div>
          <AuthView onLogin={handleLogin} />
        </div>
      );
    }
    return (
      <LandingPage
        onNavigate={(tab) => setPublicView(tab)}
        onGetStarted={() => setPublicView('login')}
      />
    );
  }

  const GuestLoginModal = () => (
    <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full relative">
        <button 
          onClick={() => {
            setShowGuestLoginPrompt(false);
            if (activeTab === 'guest-login-prompt') {
              setActiveTab('client-dashboard');
            }
          }}
          className="absolute top-4 right-4 text-stone-400 hover:text-stone-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-palm/10 text-palm rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <h3 className="text-2xl font-black text-bark mb-2">Account Required</h3>
          <p className="text-stone-500">
            You are currently exploring as a guest. To request services, book a drone flight, or interact with our team, please create an account or log in.
          </p>
        </div>
        <div className="space-y-3">
          <button 
            onClick={() => {
              setShowGuestLoginPrompt(false);
              handleLogout();
            }}
            className="w-full py-3 bg-palm text-white rounded-xl font-bold hover:bg-bark transition"
          >
            Log In / Create Account
          </button>
          <button 
            onClick={() => {
              setShowGuestLoginPrompt(false);
              if (activeTab === 'guest-login-prompt') {
                setActiveTab('client-dashboard');
              }
            }}
            className="w-full py-3 bg-stone-100 text-stone-600 rounded-xl font-bold hover:bg-stone-200 transition"
          >
            Continue Exploring
          </button>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return user.role === 'client' || user.role === 'guest' ? (
        <ClientDashboardView jobs={jobs} missions={missions} onNavigate={setActiveTab} user={user} />
      ) : (
        <DashboardView 
          jobs={jobs} 
          leads={leads} 
          expenses={expenses} 
          activities={activities} 
          onAddJob={addJob} 
          googleConnected={googleConnected} 
          onConnectGoogle={handleConnectGoogle} 
          onNavigate={setActiveTab}
          notifications={notifications}
          onClearNotifications={() => dispatch({ type: 'CLEAR_NOTIFICATIONS' })}
          onMarkNotificationRead={(id) => dispatch({ type: 'MARK_NOTIFICATION_READ', payload: id })}
          onSimulateNotification={handleSimulateNotification}
          onTriggerNotification={triggerNotification}
          opsAlerts={opsAlerts}
        />
      );
      case 'leads': return <LeadsView leads={leads.filter(l => !(l as any).isDeleted)} onConvert={convertLeadToJob} onAddLead={addLead} onUpdateLead={updateLead} onDeleteLead={deleteLead} onScheduleEstimate={scheduleEstimate} />;
      case 'clients': return <ClientsView 
                                jobs={jobs} 
                                leads={leads} 
                                contacts={contacts} 
                                googleConnected={googleConnected} 
                                onImportContacts={importContacts}
                                onConnectGoogle={handleConnectGoogle}
                                actingUser={user?.name}
                              />;
      case 'jobs': return <JobsView jobs={jobs.filter(j => !(j as any).isDeleted)} missions={missions} onUpdate={updateJob} onDeleteJob={deleteJob} crews={MOCK_CREWS} onRequestDrone={requestDroneMission} onAddJob={addJob} />;
      case 'schedule': return <ScheduleView jobs={jobs} missions={missions} user={user} crews={MOCK_CREWS} onUpdateJob={updateJob} />;
      case 'finance': return <FinanceView 
                                jobs={jobs} 
                                expenses={expenses} 
                                onAddExpense={addExpense} 
                                onUpdateJob={updateJob} 
                                user={user} 
                                timeEntries={timeEntries} 
                                onAddTimeEntry={handleAddTimeEntry} 
                                onUpdateTimeEntry={handleUpdateTimeEntry} 
                                contractorInvoices={contractorInvoices} 
                                onAddContractorInvoice={handleAddContractorInvoice} 
                                onUpdateContractorInvoice={handleUpdateContractorInvoice}
                                ledgerEntries={ledgerEntries}
                                financialRecords={financialRecords.filter((f: any) => !(f as any).isDeleted)}
                                onUpdateFinancialRecord={updateFinancialRecord}
                                onDeleteFinancialRecord={deleteFinancialRecord}
                                onAddLedgerEntry={async (entry) => {
                                  requireAuth(async () => {
                                    try {
                                      await addDoc(collection(db, 'ledger'), sanitizeForFirestore(entry));
                                      logActivity('FINANCE', `New ledger entry: ${entry.description}`);
                                    } catch (e) {
                                      handleFirestoreError(e, OperationType.WRITE, 'ledger');
                                    }
                                  });
                                }}
                                onUpdateLedgerEntry={async (entry) => {
                                  requireAuth(async () => {
                                    try {
                                      const { id, ...data } = entry;
                                      await updateDoc(doc(db, 'ledger', id), sanitizeForFirestore(data));
                                      logChange({ who: user?.name || 'System', whoRole: user?.role === 'staff' ? user.staffRole : user?.role, action: 'UPDATE', entity: 'ledger', entityId: id, entityLabel: entry.description, summary: `Ledger entry updated: ${entry.description}` });
                                    } catch (e) {
                                      handleFirestoreError(e, OperationType.WRITE, `ledger/${entry.id}`);
                                    }
                                  });
                                }}
                              />;
      case 'inventory': return <InventoryView items={inventory} onAddItem={addInventoryItem} onUpdateItem={updateInventoryItem} onDeleteItem={deleteInventoryItem} />;
      case 'change-log': return <ChangeLogView entries={changeLogEntries} />;
      case 'access': return <AccessControlView currentUser={user} actingUser={user?.name} />;
      case 'applicant-status': return <ApplicantStatusView user={user} />;
      case 'education': return <EducationView />;
      case 'crew': return <CrewDashboardView jobs={jobs} onUpdate={updateJob} crews={MOCK_CREWS} />;
      case 'team': return <TeamView contacts={contacts} actingUser={user?.name} />;
      case 'client-dashboard': return <ClientDashboardView jobs={jobs} missions={missions} onNavigate={setActiveTab} user={user} />;
      case 'client': return <ClientPortalView jobs={jobs} onUpdate={updateJob} onAddLead={addLead} user={user} onNavigate={setActiveTab} />;
      case 'client-preview': return user.role === 'staff'
        ? <StaffClientPreviewView jobs={jobs} contacts={contacts} actingUser={user?.name} onNavigate={setActiveTab} />
        : <ClientDashboardView jobs={jobs} missions={missions} onNavigate={setActiveTab} user={user} />;
      case 'property-intel': return user.role === 'staff'
        ? <Suspense fallback={<div className="p-10 text-center text-sm font-black uppercase tracking-widest text-stone-400">Loading property file…</div>}>
            <PropertyIntelligenceView jobs={jobs} contacts={contacts} actingUser={user?.name} />
          </Suspense>
        : <ClientDashboardView jobs={jobs} missions={missions} onNavigate={setActiveTab} user={user} />;
      case 'client-education': return <ClientPortalView jobs={jobs} onUpdate={updateJob} onAddLead={addLead} user={user} onNavigate={setActiveTab} initialTab="education" />;
      case 'client-vault': return <ClientPortalView jobs={jobs} onUpdate={updateJob} onAddLead={addLead} user={user} onNavigate={setActiveTab} initialTab="vault" />;
      case 'guest-login-prompt': 
        setTimeout(() => {
          setShowGuestLoginPrompt(true);
          setActiveTab('client-dashboard');
        }, 0);
        return <ClientDashboardView jobs={jobs} missions={missions} onNavigate={setActiveTab} user={user} />;
      case 'client-catalog': return <ServiceCatalogView 
                                        onClose={() => setActiveTab('client-dashboard')}
                                        onSelectService={(serviceId) => {
                                          requireAuth(() => {
                                            if (serviceId === 'consultation') {
                                              setActiveTab('client-consultation');
                                            } else {
                                              const droneServiceMap: Record<string, string> = {
                                                'drone-survey': 'Site Survey',
                                                'drone-health': 'Health Inspection',
                                                'drone-storm': 'Storm Assessment',
                                                'drone-verification': 'Post-Work Verification'
                                              };
                                              setSelectedDroneService(droneServiceMap[serviceId]);
                                              setActiveTab('client-drones');
                                            }
                                          });
                                        }} 
                                      />;
      case 'client-consultation': return <div className="max-w-4xl mx-auto py-8"><CustomerIntakeForm 
                                            onAddLead={addLead} 
                                            onClose={() => setActiveTab('client-catalog')} 
                                            onReturn={() => setActiveTab('client-dashboard')}
                                            user={user} 
                                          /></div>;
      case 'client-drones': return <ClientDroneView 
                                      jobs={jobs} 
                                      missions={missions} 
                                      user={user}
                                      missionTemplates={missionTemplates}
                                      initialServiceType={selectedDroneService}
                                      onRequestMission={(m) => {
                                        requireAuth(async () => {
                                          try {
                                            await requestMission(m);
                                            logActivity('DRONE', `New flight requested for ${jobs.find(j => j.id === m.jobId)?.address || 'Property'}`);
                                            setActiveTab('client-dashboard');
                                          } catch (e) {
                                            console.error(e);
                                          }
                                        });
                                      }}
                                    />;
      case 'community': return <CommunityHubView />;
      case 'arborlink': return <ArborLinkApp />;
      case 'training': return <EmployeeTrainingView />;
      case 'pay-it-forward': return <PayItForwardView />;
      case 'register-business': return <RegisterBusinessForm />;
      case 'drones': return <DroneManagement 
                                drones={drones} 
                                missions={missions} 
                                jobs={jobs} 
                                missionTemplates={missionTemplates}
                                flightLogs={flightLogs}
                                missionReports={missionReports}
                                onAddDrone={async (d) => {
                                  try {
                                    await addDoc(collection(db, 'drones'), sanitizeForFirestore(d));
                                    logActivity('DRONE', `New drone commissioned: ${d.model}`);
                                  } catch (e) {
                                    console.error(e);
                                  }
                                }}
                                onCreateMission={async (m) => {
                                  try {
                                    await requestMission(m);
                                    logActivity('DRONE', `New ${m.missionType} launched`);
                                  } catch (e) {
                                    console.error(e);
                                  }
                                }}
                                onUpdateMission={async (m) => {
                                  try {
                                    const oldMission = missions.find(mi => mi.id === m.id);
                                    await updateDroneMission(m.id, m);
                                    
                                    if (m.status === 'Completed' && oldMission?.status !== 'Completed') {
                                      logActivity('DRONE', `Mission completed and AI report generated`);
                                    }
                                    if (m.invoiced && !oldMission?.invoiced) {
                                      logActivity('FINANCE', `Invoice generated for Drone Mission: ${m.missionType}`);
                                      let contactId = '';
                                      const job = jobs.find(j => j.id === m.jobId);
                                      if (job) {
                                        const existingContact = contacts.find(c => c.email === job.email);
                                        if (existingContact) {
                                          contactId = existingContact.id;
                                        } else {
                                          const newContactRef = await addDoc(collection(db, 'contacts'), sanitizeForFirestore({
                                            name: job.clientName,
                                            email: job.email || '',
                                            phone: job.phone || '',
                                            address: job.address,
                                            createdAt: new Date().toISOString(),
                                            userId: user?.name || 'System'
                                          }));
                                          contactId = newContactRef.id;
                                        }
                                      }
                                      await generateMissionInvoice(m, contactId);
                                    }
                                    if (m.paid && !oldMission?.paid) {
                                      logActivity('FINANCE', `Payment received for Drone Mission: ${m.missionType}`);
                                    }
                                  } catch (e) {
                                    console.error(e);
                                  }
                                }}
                                onSimulateFlight={async (mission, pilotId) => {
                                  try {
                                    await simulateFlight(mission, pilotId);
                                    logActivity('DRONE', `Flight simulation completed for mission ${mission.id}`);
                                  } catch (e) {
                                    console.error(e);
                                  }
                                }}
                              />;
       default: return user.role === 'client' || user.role === 'guest' ? (
        <ClientDashboardView jobs={jobs} missions={missions} onNavigate={setActiveTab} user={user} />
      ) : (
        <DashboardView 
          jobs={jobs} 
          leads={leads} 
          expenses={expenses} 
          activities={activities} 
          onAddJob={addJob} 
          googleConnected={googleConnected} 
          onConnectGoogle={handleConnectGoogle} 
          onNavigate={setActiveTab}
          notifications={notifications}
          onClearNotifications={() => dispatch({ type: 'CLEAR_NOTIFICATIONS' })}
          onMarkNotificationRead={(id) => dispatch({ type: 'MARK_NOTIFICATION_READ', payload: id })}
          onSimulateNotification={handleSimulateNotification}
          onTriggerNotification={triggerNotification}
          opsAlerts={opsAlerts}
        />
      );
    }
  };

  // Requests awaiting action — enquiries still sitting at LEAD (not yet scheduled or converted).
  // This is the in-app half of the "alert me whenever someone requests an estimate" requirement.
  const pendingRequestCount = leads.filter((l: any) => !l.isDeleted && l.status === JobStatus.LEAD).length;

  const NavItem = ({ id, label, icon: Icon, badge }: { id: string, label: string, icon: any, badge?: number }) => (
    <button
      onClick={() => {
        if (user?.role === 'guest' && id !== 'client-dashboard' && id !== 'community' && id !== 'pay-it-forward') {
          setShowGuestLoginPrompt(true);
        } else {
          setActiveTab(id);
        }
      }}
      title={label}
      className={`flex items-center space-x-3 w-full px-4 py-3 rounded-lg transition-all duration-200 transform active:scale-95 ${
        activeTab === id ? 'bg-gecko text-white shadow-lg shadow-gecko/20' : 'text-stone-500 hover:bg-leaf/20'
      }`}
    >
      <Icon />
      <span className={`${!isSidebarOpen && 'hidden'} font-medium whitespace-nowrap`}>{label}</span>
      {!!badge && badge > 0 && (
        <span className={`ml-auto min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center`}>
          {badge}
        </span>
      )}
    </button>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-stone-50">
      {showTour && <OnboardingTour onComplete={() => setShowTour(false)} />}
      {showGuestLoginPrompt && <GuestLoginModal />}
      
      <aside className={`${isSidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-stone-200 transition-all duration-300 flex flex-col`}>
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Logo className="w-10 h-10 flex-shrink-0" />
            <BrandName className={`${!isSidebarOpen && 'hidden'} text-xl text-palm`} />
          </div>
          <button onClick={() => setSidebarOpen(!isSidebarOpen)} className="text-stone-400 hover:text-palm">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
        
        <nav className="flex-1 min-h-0 px-3 space-y-1 overflow-y-auto overflow-x-hidden pb-4">
          {user.role === 'staff' ? (
            <>
              {user.staffRole === 'admin' && (
                <>
                  <NavItem id="dashboard" label="Admin Center" icon={Icons.Dashboard} />
                  <NavItem id="clients" label="Clients" icon={Icons.Safety} />
                  <NavItem id="client-preview" label="Customer View" icon={Icons.Dashboard} />
                  <NavItem id="property-intel" label="Property Intel" icon={Icons.Tree} />
                  <NavItem id="leads" label="CRM & Leads" icon={Icons.Lead} badge={pendingRequestCount} />
                  <NavItem id="jobs" label="Operations" icon={Icons.Calendar} />
                  <NavItem id="inventory" label="Inventory" icon={Icons.Tree} />
                  <NavItem id="change-log" label="Change Log" icon={Icons.Education} />
                  <NavItem id="access" label="Users & Access" icon={Icons.Safety} />
                  <NavItem id="drones" label="Aerial Intelligence" icon={Icons.Drone} />
                </>
              )}
              
              {(user.staffRole === 'employee' || user.staffRole === 'contractor' || user.staffRole === 'admin') && (
                <>
                  <NavItem id="crew" label="Crew Dashboard" icon={Icons.Dashboard} />
                  <NavItem id="team" label="Team" icon={Icons.Safety} />
                  {user.staffRole !== 'admin' && <NavItem id="client-preview" label="Customer View" icon={Icons.Dashboard} />}
                </>
              )}

              <NavItem id="schedule" label="Schedule" icon={Icons.Calendar} />
              
              {(user.staffRole === 'admin' || user.staffRole === 'employee') && (
                <>
                  <NavItem id="training" label="Training Hub" icon={Icons.Education} />
                  <NavItem id="education" label="Education" icon={Icons.Education} />
                </>
              )}
              
              <NavItem id="finance" label="Financials" icon={Icons.Finance} />
              <NavItem id="arborlink" label="ArborLink Network" icon={Icons.Tree} />
              <NavItem id="pay-it-forward" label="Pay It Forward" icon={Icons.Heart} />
            </>
          ) : user.role === 'applicant' ? (
            <>
              <NavItem id="applicant-status" label="Application Status" icon={Icons.Education} />
            </>
          ) : (
            <>
              <NavItem id="client-dashboard" label="Dashboard" icon={Icons.Dashboard} />
              <NavItem id="client" label="Customer Hub" icon={Icons.Safety} />
              <NavItem id="education" label="Education" icon={Icons.Education} />
              <NavItem id="client-drones" label="Drone Services" icon={Icons.Drone} />
              <NavItem id="arborlink" label="ArborLink Network" icon={Icons.Tree} />
              <NavItem id="community" label="Local Area" icon={Icons.Tree} />
              <NavItem id="pay-it-forward" label="Pay It Forward" icon={Icons.Heart} />
              <NavItem id="register-business" label="Register Your Business" icon={Icons.Tree} />
            </>
          )}

          {/* Public tools — the standalone sites, opened in a new tab so the dashboard keeps its state */}
          <div className="pt-4 mt-4 border-t border-stone-100">
            <div className={`${!isSidebarOpen && 'hidden'} px-4 pb-2 text-[10px] font-black uppercase tracking-widest text-stone-400`}>Public Tools</div>
            {[
              { view: 'vox', label: 'Vox', icon: Icons.Dashboard },
              { view: 'arborai', label: 'ArborAI', icon: Icons.Education },
              { view: 'worksheetz', label: 'Worksheetz', icon: Icons.Safety },
              { view: 'arborlink', label: 'ArborLink', icon: Icons.Tree },
            ].map((t) => (
              <button
                key={t.view}
                onClick={() => setPublicView(t.view)}
                title={t.label}
                className="w-full flex items-center space-x-3 px-4 py-2 rounded-xl text-stone-500 hover:text-palm hover:bg-stone-50 transition"
              >
                <t.icon />
                <span className={`${!isSidebarOpen && 'hidden'} text-sm font-medium whitespace-nowrap`}>{t.label}</span>
              </button>
            ))}
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              title="Public Site"
              className="w-full flex items-center space-x-3 px-4 py-2 rounded-xl text-stone-500 hover:text-palm hover:bg-stone-50 transition"
            >
              <Icons.Dashboard />
              <span className={`${!isSidebarOpen && 'hidden'} text-sm font-medium whitespace-nowrap`}>Public Site</span>
              <span className={`${!isSidebarOpen && 'hidden'} ml-auto text-stone-300 text-xs`}>↗</span>
            </a>
          </div>
        </nav>

        <div className="p-4 border-t border-stone-100 space-y-2">
          <a
            href="https://brandtmgreen.gumroad.com/l/budhwy"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center space-x-3 px-4 py-2 text-gecko hover:text-palm transition bg-gecko/10 rounded-lg hover:bg-gecko/20"
            title="Get Our New Book"
          >
            <Icons.Education />
            <span className={`${!isSidebarOpen && 'hidden'} text-xs font-bold uppercase whitespace-nowrap`}>Get Our Book</span>
          </a>
          {user.role === 'client' && (
            <button 
              onClick={() => setShowTour(true)}
              title="Interactive Tour"
              className="w-full flex items-center space-x-3 px-4 py-2 text-stone-400 hover:text-palm transition"
            >
              <Icons.Safety />
              <span className={`${!isSidebarOpen && 'hidden'} text-xs font-bold uppercase whitespace-nowrap`}>Interactive Tour</span>
            </button>
          )}
          <button 
            onClick={handleLogout}
            title="Sign Out"
            className="w-full flex items-center space-x-3 px-4 py-2 text-red-400 hover:text-red-600 transition"
          >
            <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span className={`${!isSidebarOpen && 'hidden'} text-xs font-bold uppercase whitespace-nowrap`}>Sign Out</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="bg-gecko text-white px-4 py-2 text-center text-sm font-bold flex justify-center items-center gap-2 z-50 relative">
          <span>📚 We just published a new book!</span>
          <a href="https://brandtmgreen.gumroad.com/l/budhwy" target="_blank" rel="noopener noreferrer" className="underline text-white hover:text-gold transition-colors">
            Get your copy here
          </a>
        </div>
        <header className="sticky top-0 z-40 bg-stone-50/80 backdrop-blur-md px-8 py-6 border-b border-stone-200 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Logo className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-black text-bark capitalize tracking-tight">{activeTab.replace('-', ' ')}</h1>
              <p className="text-stone-500 text-sm font-medium">System Status: <span className="text-gecko">Operational</span></p>
            </div>
          </div>
          <div className="flex items-center space-x-6">
            {user && (
              <>
                <div className="hidden md:flex flex-col items-end">
                  <span className="text-xs font-black text-stone-400 uppercase tracking-widest">{user.role} Authorization</span>
                  <span className="text-sm font-bold text-bark">{user.name}</span>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-white border border-stone-200 flex items-center justify-center text-palm font-black text-xl shadow-sm">
                  {user?.name?.charAt(0) || '?'}
                </div>
              </>
            )}
          </div>
        </header>

        <div className="p-8 max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
      
      {/* Floating Notification Toasts Stack */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 max-w-sm pointer-events-none">
        <AnimatePresence>
          {activeToasts.map(toast => {
            const meta = notificationMeta(toast.type);
            return (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 100, transition: { duration: 0.2 } }}
              className="pointer-events-auto bg-stone-900 border border-stone-800 text-white rounded-3xl p-4 shadow-2xl flex gap-3 items-start relative overflow-hidden min-w-[300px]"
            >
              <div className={`absolute top-0 bottom-0 left-0 w-2 ${meta.bar}`} />
              
              <div className="flex-1 pl-1">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className={`text-[9px] uppercase tracking-widest font-black ${meta.text}`}>
                    {meta.glyph} {meta.label}
                  </span>
                  <button 
                    onClick={() => dispatch({ type: 'REMOVE_TOAST', payload: toast.id })}
                    className="text-stone-400 hover:text-white transition rounded-full p-0.5 hover:bg-white/10"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
                <h5 className="text-xs font-black text-white leading-tight mb-1">{toast.title}</h5>
                <p className="text-[11px] font-medium text-stone-300 leading-normal">{toast.message}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[9px] font-semibold text-stone-500">
                    {new Date(toast.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                  <span className="text-[9px] font-black tracking-widest uppercase text-gecko bg-gecko/10 px-2 py-0.5 rounded-full">
                    Arb-E Push
                  </span>
                </div>
              </div>
            </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Persistent Arb-E Chat Widget */}
      {user && <ArbEWidget jobs={jobs} user={user} expenses={expenses} onAddExpense={addExpense} googleConnected={googleConnected} />}
    </div>
  );
};

export default App;
